import { toast } from '@affine/component';
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import {
  DEFAULT_DOC_MODE,
  DocsService,
  isCreateDocPermissionError,
} from '@affine/core/modules/doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { type DocMode } from '@blocksuite/affine/model';
import { useServices } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import type { DocCreateOptions } from '../../modules/doc/types';

export const usePageHelper = () => {
  const { docsService, workbenchService, appSidebarService } = useServices({
    DocsService,
    WorkbenchService,
    AppSidebarService,
  });
  const workbench = workbenchService.workbench;
  const docRecordList = docsService.list;
  const appSidebar = appSidebarService.sidebar;

  const createPageAndOpen = useCallback(
    (
      mode?: DocMode,
      options: {
        at?: 'new-tab' | 'tail' | 'active';
        show?: boolean;
        targetFolderId?: DocCreateOptions['targetFolderId'];
        createInWorkspaceRoot?: DocCreateOptions['createInWorkspaceRoot'];
        title?: DocCreateOptions['title'];
      } = {
        at: 'active',
        show: true,
      }
    ) => {
      const {
        at = 'active',
        show = true,
        targetFolderId,
        createInWorkspaceRoot,
        title,
      } = options;
      appSidebar.setHovering(false);
      let page;
      try {
        page = docsService.createDoc({
          targetFolderId,
          createInWorkspaceRoot,
          title,
          userInitiated: true,
          funnelCoreJobType: 'document_created',
        });
      } catch (error) {
        if (isCreateDocPermissionError(error)) {
          toast('Keine Berechtigung zum Erstellen von Dokumenten.');
        }
        throw error;
      }

      if (mode) {
        docRecordList.doc$(page.id).value?.setPrimaryMode(mode);
      }

      if (show !== false) {
        workbench.openDoc(
          {
            docId: page.id,
            mode: docRecordList.getPrimaryMode(page.id) ?? DEFAULT_DOC_MODE,
          },
          {
            at,
            show,
          }
        );
      }
      return page;
    },
    [appSidebar, docRecordList, docsService, workbench]
  );

  const createEdgelessAndOpen = useCallback(
    (
      options: {
        at?: 'new-tab' | 'tail' | 'active';
        show?: boolean;
        targetFolderId?: DocCreateOptions['targetFolderId'];
        createInWorkspaceRoot?: DocCreateOptions['createInWorkspaceRoot'];
        title?: DocCreateOptions['title'];
      } = {
        at: 'active',
        show: true,
      }
    ) => {
      return createPageAndOpen('edgeless', options);
    },
    [createPageAndOpen]
  );

  return useMemo(() => {
    return {
      createPage: (
        mode?: DocMode,
        options?: {
          at?: 'new-tab' | 'tail' | 'active';
          show?: boolean;
          targetFolderId?: DocCreateOptions['targetFolderId'];
          createInWorkspaceRoot?: DocCreateOptions['createInWorkspaceRoot'];
          title?: DocCreateOptions['title'];
        }
      ) => createPageAndOpen(mode, options),
      createEdgeless: createEdgelessAndOpen,
    };
  }, [createEdgelessAndOpen, createPageAndOpen]);
};
